import React from 'react';
import { Landmark, Phone, ExternalLink, AlertTriangle } from 'lucide-react';
import { EMBASSY_DATA } from '@/data/essentials_b';
import { usePassport, COUNTRY_LABELS } from '@/context/PassportContext';

export default function EmbassyCard({ destCountryCode, destCountryName }) {
  const { origin } = usePassport();
  if (!destCountryCode) return null;
  const destCC = destCountryCode.toUpperCase();
  const bucket = EMBASSY_DATA[destCC] || {};
  const d = bucket[origin];
  if (origin === destCC) return null; // home country
  return (
    <div className="puvio-card p-5" data-testid="embassy-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-bold text-[11px] uppercase tracking-[0.22em] text-[var(--puvio-mint-deep)]">Your embassy here</p>
          <p className="text-xs text-[var(--puvio-text-2)] mt-0.5 font-medium">{COUNTRY_LABELS[origin] || origin} mission in {destCountryName || destCC}</p>
        </div>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#D2F4E5' }}>
          <Landmark className="w-6 h-6 text-[var(--puvio-mint-deep)]" />
        </div>
      </div>
      {!d ? (
        <p className="mt-4 text-sm text-[var(--puvio-text-2)]">Embassy data not yet available for this route.</p>
      ) : (
        <>
          <p className="mt-4 text-sm font-extrabold text-[#0F1A13]" data-testid="embassy-name">{d.name}</p>
          <p className="mt-1 text-[12px] text-[var(--puvio-text-2)] leading-relaxed">{d.address}</p>
          {d.hours && <p className="mt-1 text-[11px] text-[var(--puvio-text-3)] font-medium">{d.hours}</p>}
          <div className="mt-3 flex flex-col gap-2">
            <a href={`tel:${d.phone}`} className="flex items-center justify-between px-3 py-2.5 rounded-2xl bg-[var(--puvio-bg-soft)] border border-[var(--puvio-border)] puvio-focus" data-testid="embassy-phone">
              <span className="text-[12px] font-bold uppercase tracking-wide text-[var(--puvio-text-2)]">Main line</span>
              <span className="flex items-center gap-2 font-extrabold text-[#0F1A13]">{d.phone}<Phone className="w-3.5 h-3.5 text-[var(--puvio-mint-deep)]" /></span>
            </a>
            {d.emergency && (
              <a href={`tel:${d.emergency}`} className="flex items-center justify-between px-3 py-2.5 rounded-2xl bg-[var(--puvio-coral-deep)] text-[#0F1A13] puvio-focus" data-testid="embassy-emergency">
                <span className="text-[12px] font-bold uppercase tracking-wide">24/7 emergency</span>
                <span className="flex items-center gap-2 font-extrabold">{d.emergency}<Phone className="w-3.5 h-3.5" /></span>
              </a>
            )}
            {d.website && (
              <a href={`https://${d.website}`} target="_blank" rel="noreferrer" className="flex items-center justify-between px-3 py-2.5 rounded-2xl bg-[var(--puvio-bg-soft)] border border-[var(--puvio-border)] puvio-focus" data-testid="embassy-website">
                <span className="text-[12px] font-bold uppercase tracking-wide text-[var(--puvio-text-2)]">Website</span>
                <span className="flex items-center gap-2 font-extrabold text-[#0F1A13]">{d.website}<ExternalLink className="w-3.5 h-3.5 text-[var(--puvio-mint-deep)]" /></span>
              </a>
            )}
          </div>
          <div className="mt-4 rounded-2xl bg-amber-400/15 border border-[#F6C84F] p-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-[#B85F31] inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Lost passport?</p>
            <ol className="mt-1.5 list-decimal list-inside space-y-0.5 text-[12px] text-[#0F1A13] font-medium">
              <li>File a police report — keep the FIR copy.</li>
              <li>Call the 24/7 emergency line above.</li>
              <li>Gather a photo ID + 2 photos + flight ticket.</li>
              <li>Apply for an Emergency Certificate (EC) at the embassy.</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
}
